import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

export const DeleteStaffDialog = ({ staffData, token, apiCall, onDone }) => {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const openDialog = async () => {
    setOpen(true);
    setReason('');
    try {
      const data = await apiCall(`/gdpr/staff/${staffData.user.id}/deletion-preview`, 'GET', null, token);
      setPreview(data);
    } catch {
      toast.error('Failed to load deletion preview');
    }
  };

  const archive = async () => {
    if (reason.trim().length < 5) { toast.error('Please give a reason for deletion (min 5 characters)'); return; }
    setBusy(true);
    try {
      await apiCall(`/admin/staff/${staffData.user.id}?reason=${encodeURIComponent(reason)}`, 'DELETE', null, token);
      toast.success(`${staffData.user.name} archived. Records retained per the retention schedule.`);
      setOpen(false);
      onDone();
    } catch {
      toast.error('Failed to archive staff member');
    }
    setBusy(false);
  };

  const anonymise = async () => {
    if (reason.trim().length < 5) { toast.error('Please give a reason for anonymisation (min 5 characters)'); return; }
    if (!window.confirm('Anonymise permanently? Documents and signed PDFs will be deleted from storage and identity data erased. This cannot be undone.')) return;
    setBusy(true);
    try {
      const res = await apiCall(`/gdpr/staff/${staffData.user.id}/anonymise`, 'POST', { reason }, token);
      toast.success(res.message);
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(e.detail || 'Anonymisation failed');
    }
    setBusy(false);
  };

  return (
    <>
      <Button variant="destructive" size="sm" onClick={openDialog} data-testid={`delete-staff-btn-${staffData.user.id}`}>
        Delete Staff
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Delete / anonymise {staffData.user.name}</DialogTitle>
            <DialogDescription>Review linked records before confirming. A reason is mandatory and will be audit-logged.</DialogDescription>
          </DialogHeader>
          {!preview ? <p className="text-sm text-slate-500">Loading linked records…</p> : (
            <div className="space-y-4 text-sm" data-testid="deletion-preview">
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-slate-50 rounded p-2">Timesheets: <b>{preview.timesheets}</b></div>
                <div className="bg-slate-50 rounded p-2">Shifts: <b>{preview.shifts}</b></div>
                <div className="bg-slate-50 rounded p-2">Assignments: <b>{preview.assignments}</b></div>
                <div className="bg-slate-50 rounded p-2">Signed documents: <b>{preview.signed_documents}</b></div>
              </div>
              {preview.documents.length > 0 && (
                <div>
                  <p className="font-medium mb-1">Uploaded documents ({preview.documents.length}):</p>
                  <ul className="space-y-1">
                    {preview.documents.map((d) => (
                      <li key={`${d.document_type}-${d.filename}`} className="flex items-center gap-2">
                        <span>{d.document_type} — {d.filename}</span>
                        {d.sensitive && <Badge className="bg-red-600 text-[10px]">RESTRICTED</Badge>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="bg-amber-50 border border-amber-200 rounded p-3 space-y-1">
                {preview.retention_warnings.map((w) => <p key={w.slice(0, 40)} className="text-xs text-amber-800">• {w}</p>)}
              </div>
              <div>
                <p className="font-medium mb-1">Reason for deletion (required, audit-logged):</p>
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                  placeholder="e.g. Engagement ended; erasure request DSR-123" data-testid="deletion-reason-input" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button variant="secondary" disabled={busy || reason.trim().length < 5} onClick={archive} data-testid="confirm-archive-btn">
                  Archive (soft delete)
                </Button>
                <Button variant="destructive" disabled={busy || reason.trim().length < 5} onClick={anonymise} data-testid="confirm-anonymise-btn">
                  Anonymise (GDPR erasure)
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
